import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Button,
  Chip,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CloudUpload as DeployIcon,
  Undo as RollbackIcon,
} from '@mui/icons-material';

const ensureHttps = (url) => {
  if (url.startsWith('/')) return url;
  return url.replace(/^http:/, 'https:');
};

const secureFetch = async (url, options = {}) => {
  return fetch(ensureHttps(url), { ...options, credentials: 'same-origin' });
};

const shortId = (id) => {
  if (!id) return '—';
  const hex = id.includes(':') ? id.split(':')[1] : id;
  return hex.slice(0, 12);
};

const TERMINAL = ['succeeded', 'succeeded-unconfirmed', 'failed'];

export default function DeploymentTab() {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'deploy' | 'rollback'
  const [deploy, setDeploy] = useState(null); // active deploy/rollback record
  const deployTimer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await secureFetch('/deployment/status');
      const body = await res.json();
      if (res.ok) {
        setStatus(body);
        setStatusError(null);
      } else {
        setStatusError(body.error || `status ${res.status}`);
      }
    } catch (e) {
      setStatusError(e.message);
    }
  }, []);

  // Poll overall status while idle.
  useEffect(() => {
    loadStatus();
    const t = setInterval(() => {
      if (!deploy || TERMINAL.includes(deploy.status)) loadStatus();
    }, 5000);
    return () => clearInterval(t);
  }, [loadStatus, deploy]);

  const pollDeploy = useCallback((id) => {
    clearInterval(deployTimer.current);
    deployTimer.current = setInterval(async () => {
      try {
        const res = await secureFetch(`/deployment/deploy/${id}`);
        if (!res.ok) return; // puffercloud may be mid-restart; keep polling
        const rec = await res.json();
        setDeploy(rec);
        if (TERMINAL.includes(rec.status)) {
          clearInterval(deployTimer.current);
          loadStatus();
        }
      } catch {
        /* transient during the puffercloud restart -- keep polling */
      }
    }, 2000);
  }, [loadStatus]);

  useEffect(() => () => clearInterval(deployTimer.current), []);

  const runCheck = async () => {
    setBusyAction(true);
    try {
      const res = await secureFetch('/deployment/check', { method: 'POST' });
      const body = await res.json();
      if (res.ok) {
        setStatus(body);
        setStatusError(null);
      } else {
        setStatusError(body.error || `status ${res.status}`);
      }
    } catch (e) {
      setStatusError(e.message);
    } finally {
      setBusyAction(false);
    }
  };

  const startAction = async (kind) => {
    setConfirm(null);
    setBusyAction(true);
    try {
      const res = await secureFetch(`/deployment/${kind}`, { method: 'POST' });
      const body = await res.json();
      if (res.ok && body.deployId) {
        setDeploy({ id: body.deployId, status: 'running', kind, log: [] });
        pollDeploy(body.deployId);
      } else {
        setStatusError(body.error || `status ${res.status}`);
      }
    } catch (e) {
      setStatusError(e.message);
    } finally {
      setBusyAction(false);
    }
  };

  const inProgress = deploy && !TERMINAL.includes(deploy.status);
  const updateAvailable = status?.updateAvailable;

  const statusColor = (s) =>
    s === 'succeeded' ? 'success'
      : s === 'failed' ? 'error'
        : s === 'succeeded-unconfirmed' ? 'warning'
          : 'default';

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Image deployment</Typography>
          {statusError && <Chip color="error" label={`Agent: ${statusError}`} />}
          {updateAvailable && !statusError && <Chip color="info" label="Update available" />}
        </Stack>

        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <b>Image:</b> {status?.imageRef || '—'}
          </Typography>
          <Typography variant="body2">
            <b>Running:</b> {shortId(status?.running?.imageId)}
          </Typography>
          <Typography variant="body2">
            <b>Available (pulled):</b> {shortId(status?.available?.imageId)}
          </Typography>
          <Typography variant="body2">
            <b>Rollback target:</b> {shortId(status?.previous?.imageId)}
          </Typography>
          {status?.lastDeploy && (
            <Typography variant="body2" component="div">
              <b>Last deploy:</b>{' '}
              <Chip
                size="small"
                color={statusColor(status.lastDeploy.status)}
                label={status.lastDeploy.status}
              />{' '}
              {status.lastDeploy.finishedAt}
            </Typography>
          )}
          {status?.lastCheck && (
            <Typography variant="body2">
              <b>Last checked:</b> {status.lastCheck}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={runCheck}
            disabled={busyAction || inProgress || status?.busy}
          >
            Check now
          </Button>
          <Button
            variant="contained"
            startIcon={<DeployIcon />}
            onClick={() => setConfirm('deploy')}
            disabled={busyAction || inProgress || status?.busy || !updateAvailable}
          >
            Deploy
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RollbackIcon />}
            onClick={() => setConfirm('rollback')}
            disabled={busyAction || inProgress || status?.busy || !status?.previous?.imageId}
          >
            Roll back to previous
          </Button>
        </Stack>
      </Paper>

      {deploy && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {deploy.kind === 'rollback' ? 'Rollback' : 'Deploy'} {deploy.id}
            </Typography>
            {inProgress && <CircularProgress size={20} />}
            <Chip color={statusColor(deploy.status)} label={deploy.status} />
          </Stack>
          {deploy.backupPath && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>DB backup:</b> {deploy.backupPath}
            </Typography>
          )}
          {deploy.error && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {deploy.error}
            </Typography>
          )}
          <Divider sx={{ mb: 1 }} />
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: '#0d1117',
              color: '#c9d1d9',
              fontSize: 12,
              borderRadius: 1,
              maxHeight: 360,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {(deploy.log || []).join('\n') || 'Waiting for output…'}
          </Box>
        </Paper>
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm === 'rollback' ? 'Roll back to previous image?' : 'Deploy the pulled image?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === 'rollback'
              ? 'The puffercloud container will be recreated from the previously running image. The database is NOT restored automatically.'
              : 'A database backup is taken, then the puffercloud container is recreated from the newly pulled image. It runs database migrations on startup.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm === 'rollback' ? 'warning' : 'primary'}
            onClick={() => startAction(confirm)}
          >
            {confirm === 'rollback' ? 'Roll back' : 'Deploy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
